import { getNewCartList, getSubTotalPrice } from '@/api/cart';
import { ICartGroup, ITotalData } from '@/api/types/cart';
import {
  createDefaultAutoOfferStore,
  normalizeAppliedOffer,
  type AutoOfferStore,
} from '@/domain/cart/auto-offer';
import { getCartExperimentPolicy } from '@/domain/cart/experiment';
import { CART_POPOVER_EXPERIMENT_OVERRIDE } from '@/domain/cart/experiment/config';
import { resolveBrowserCartExperiment } from '@/domain/cart/experiment/client';
import {
  getCartOfferScope,
  readCartOfferIntent,
  readGuestCartOfferSkip,
  writeCartOfferIntent,
} from '@/domain/cart/auto-offer/persistence';
import { requestCartOfferQuote } from '@/domain/cart/auto-offer/quote';
import { ProviderType, ReportingCenter } from '@/reporting';
import { EmarsysProvider } from '@/reporting/providers/emarsysProvider';
import { Store } from '@/store';
import logger from '@/utils/client-logger';
import { IS_US, MOBILE_SCREEN_WIDTH } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { genItemPrice } from '@/utils/price';
import { checkHeaderConfig, setLocal } from '@/utils/tools';
import { StateCreator } from 'zustand';

// Cookie获取工具函数
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

// 这些接口移到了对应的组件中使用，不再需要全局定义

export type PendingCartGroup = ICartGroup & { pendingKind?: 'prescription' };

export interface CartState {
  /** 当前身份下的购物车列表是否已完成一次写入 */
  cartDataReady: boolean;
  // 可结算购物车列表 。 第一购物车列表 表示第一购物车（Out of Stock & Invalid Items 上面的购物车列表）渲染的所有品。 跟接口的 list 字段对应
  canBuyList: ICartGroup[];
  // 可结算购物车条目列表（可勾选的）。 所有可勾选的购物车列表。 跟接口的 can_buy_list 字段对应
  canBuyCartsList: ICartGroup[];
  // 待配镜购物车列表。只用于 Frame List 展示，不参与勾选、计价和结算
  pendingLensList: PendingCartGroup[];
  // 不可结算购物车列表 。 表示第二购物车（Out of Stock & Invalid Items 下面的购物车列表）渲染的所有品. 跟接口的 can_not_sell_list 字段对应
  canNotBuyList: ICartGroup[];
  // 选中的购物车商品ID列表
  selectedCartIds: number[];
  // 购物车总价信息
  subTotal: ITotalData;
  cartPopoverPriceStatus: 'idle' | 'loading' | 'success' | 'error';
  cartPopoverOffer?: AutoOfferStore;
  cartPopoverPriceKey?: string;
  // 这些配置现在直接从 publicResourceData 获取，不再需要单独的 state
  // 赠品相关数据
  giftData: {
    should_pop: boolean;
    is_pick_up: boolean;
    cart: any;
  };
  giftCount: number;
  uuidOrderList: number[];
}

export interface CartActions {
  // 设置购物车数据
  setCanBuyList: (canBuyList: CartState['canBuyList']) => void;
  setCanBuyCartsList: (canBuyCartsList: CartState['canBuyCartsList']) => void;
  setPendingLensList: (pendingLensList: CartState['pendingLensList']) => void;
  setCanNotBuyList: (canNotBuyList: CartState['canNotBuyList']) => void;
  setSelectedCartIds: (selectedCartIds: CartState['selectedCartIds']) => void;
  setSubTotal: (subTotal: Omit<ITotalData, 'applied_offer'> & { applied_offer?: any }) => void;
  setGiftData: (giftData: CartState['giftData']) => void;
  setGiftCount: (giftCount: CartState['giftCount']) => void;

  // 配置相关方法已移除，直接从 publicResourceData 获取

  // Vue项目对应的核心方法
  setCart: (cartdata?: any, options?: { effects: 'data-only' }) => Promise<any>;
  cartTotalPrice: () => Promise<void>;
  isCartPopoverQuoteCurrent: () => boolean;
  setUuidOrderList: (uuidOrderList: number[]) => void;
}

export type CartSlice = CartState & CartActions;

export const initialCartState: CartState = {
  cartDataReady: false,
  cartPopoverPriceStatus: 'idle',
  canBuyList: [],
  canBuyCartsList: [],
  pendingLensList: [],
  canNotBuyList: [],
  selectedCartIds: [],
  subTotal: {
    total: '0.00',
    discount: '0.00',
    code_discount: '0.00',
    point_discount: '0.00',
    subtotal: '0.00',
  },
  giftData: {
    should_pop: false,
    is_pick_up: false,
    cart: null,
  },
  giftCount: 0,
  uuidOrderList: [],
};

export const createCartSlice: StateCreator<Store, [], [], CartSlice> = (set, get, storeApi) => {
  let quoteVersion = 0;
  let quoteIsCurrent: (() => boolean) | undefined;
  return {
    ...initialCartState,
    isCartPopoverQuoteCurrent: () => Boolean(quoteIsCurrent?.()),

    // 基础设置方法
    setCanBuyList: (canBuyList) => {
      set({ canBuyList });
      setLocal('zl-canBuyList', canBuyList);
    },
    setCanBuyCartsList: (canBuyCartsList) => {
      set({ canBuyCartsList });
      setLocal('zl-canBuyCartsList', canBuyCartsList);
    },
    setPendingLensList: (pendingLensList) => {
      set({ pendingLensList });
    },
    setCanNotBuyList: (canNotBuyList) => {
      set({ canNotBuyList });
    },
    setSubTotal: (subTotal) =>
      set({
        subTotal: {
          ...subTotal,
          applied_offer: subTotal.applied_offer
            ? normalizeAppliedOffer(subTotal.applied_offer)
            : undefined,
        },
      }),
    setGiftData: (giftData) => set({ giftData }),
    setGiftCount: (giftCount) => set({ giftCount }),
    setUuidOrderList: (uuidOrderList: number[]) => {
      set({ uuidOrderList });
      setLocal('zl-uuidOrderList', uuidOrderList);
    },
    setSelectedCartIds: (selectedCartIds) => {
      set({ selectedCartIds });
      setLocal('zl-selectedCart', selectedCartIds);
    },
    // === Vue项目cart.js中对应的actions ===
    setCart: async (cartdata, options) => {
      // 列表请求开始即作废旧报价，不能等列表返回后才阻止 MiniCart 使用旧金额结算。
      ++quoteVersion;
      quoteIsCurrent = undefined;
      set({
        cartDataReady: false,
        cartPopoverPriceStatus: 'loading',
        cartPopoverPriceKey: undefined,
        cartPopoverOffer: undefined,
      });
      const {
        setCanBuyList,
        setCanBuyCartsList,
        setPendingLensList,
        setCanNotBuyList,
        setGiftData,
        setGiftCount,
        cartTotalPrice,
      } = get() as CartSlice;

      let data: any = {};
      if (cartdata) {
        data = cartdata;
      } else {
        // 调用购物车列表接口
        const response = await getNewCartList({
          same_spu: typeof window !== 'undefined' && window.location.pathname === '/cart',
        });
        if (response.code !== undefined && response.code !== 200)
          throw new Error(response.msg || 'Cart list request failed');
        data = response.data;
      }
      const canBuyCartsList = data.can_buy_list || [];

      // 处理购物车数据格式（对应Vue项目中的cartInforEdit函数）
      if (data.list) {
        data.list = data.list.map((ele: any) => {
          return {
            ...ele,
            carts: ele.carts.map((item: any) => {
              return {
                ...item,
                lenseShow: false,
              };
            }),
            selected: false,
            disabled: true, // 是否可以选中
          };
        });
      }

      if (data.can_not_sell_list) {
        data.can_not_sell_list = data.can_not_sell_list.map((ele: any) => {
          return {
            ...ele,
            lenseShow: false,
            selected: false,
          };
        });
      }

      // 更新store状态
      setGiftData(data.new_gift_popup || { should_pop: false, is_pick_up: false, cart: null });
      setGiftCount(data.total_gift_count || 0);
      setCanBuyList(data.list || []);
      setCanBuyCartsList(canBuyCartsList);
      const pendingLensList = pendingLensGroups(data);
      setPendingLensList(pendingLensList);
      setCanNotBuyList(data.can_not_sell_list || []);
      set({ cartDataReady: true });

      // 更新pc端用户顶栏购物车浮窗展示价格（此处为全部勾选时的价格）
      // 只有在PC端且不是购物车页面的时候才调用
      if (options?.effects !== 'data-only' && typeof window !== 'undefined') {
        const deviceType = getCookie('deviceType') || String(EPlatform.WAP); // 默认为WAP
        /**
         * todo:
         * 赵艺的电脑 windows，不知道为什么不会发起 cartTotalPrice 的请求，但是她那访问主站线上又是好的。
         * 本地始终无法复现，试试屏幕宽度替代 cookie 中的方式。
         * https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/bug/a6c79d4ffbbb86c1c7f0088b0c
         */
        const isPC =
          window.innerWidth >= MOBILE_SCREEN_WIDTH || deviceType === String(EPlatform.WEB); // WEB端是1
        // const isCartPage = window.location.pathname.includes('/cart');

        if (isPC) {
          await cartTotalPrice();
        }
      }

      // Emarsys埋点
      if (options?.effects !== 'data-only' && typeof window !== 'undefined') {
        const center = ReportingCenter.getInstance();
        const emarsysProvider = center.getProvider(ProviderType.EMARSYS) as EmarsysProvider;
        const arr = [];
        canBuyCartsList.forEach((item) => {
          const cartData = item.carts?.[0];
          if (!cartData?.shop_sku) return;
          arr.push({
            item: cartData.shop_sku, // 购物车ID
            quantity: cartData.qty, // 数量
            price: parseFloat(genItemPrice(cartData, false, env.NEXT_PUBLIC_DEFAULT_CURRENCY)), // 以美元为基础的售价
          });
        });
        if (emarsysProvider) {
          // ProviderType.EMARSYS
          emarsysProvider.track({
            eventName: 'cart',
            properties: arr,
          });
        }
      }

      return {
        canBuyList: data.list || [],
        canBuyCartsList,
        pendingLensList,
        canNotBuyList: data.can_not_sell_list || [],
      };
    },
    // 用于pc端购物车悬浮窗展示价格
    cartTotalPrice: async () => {
      const version = ++quoteVersion;
      quoteIsCurrent = undefined;
      // PC Header 在支付等路由仅被 CSS 隐藏，账户就绪 effect / setCart 仍可能调用这里。
      // 复用 Header 的路由规则，在解析实验前跳过 MiniCart 报价，避免用已下单的旧商品再次计价。
      // 只撤销浮窗报价资格；商品、已存金额和用户选券意图仍由原有业务维护。
      const isHeaderHidden = () =>
        typeof window !== 'undefined' &&
        !checkHeaderConfig(window.location.pathname, false).isShowHeaderNav;
      if (isHeaderHidden()) {
        set({
          cartPopoverPriceStatus: 'idle',
          cartPopoverOffer: undefined,
          cartPopoverPriceKey: undefined,
        });
        return;
      }
      const initial = get();
      const identity = (state: Store) =>
        JSON.stringify([
          state.token,
          state.userInfo?.id,
          state.clientTokenReady,
          state.userInfoReady,
        ]);
      const identityKey = identity(initial);
      const currencyName = initial.currency?.name;
      const items = initial.canBuyCartsList;
      const ids = items.flatMap((group) => group.carts.map((item) => item.cart_id));
      const priceKey = String(version);
      const current = () =>
        version === quoteVersion &&
        !isHeaderHidden() &&
        get().cartDataReady &&
        identity(get()) === identityKey &&
        get().currency?.name === currencyName &&
        get().canBuyCartsList === items;
      set({
        cartPopoverPriceStatus: 'loading',
        cartPopoverOffer: undefined,
        cartPopoverPriceKey: undefined,
      });
      if (!ids.length || !currencyName) {
        set({ subTotal: initialCartState.subTotal, cartPopoverPriceStatus: 'idle' });
        return;
      }
      try {
        // 固定浮窗分组时不请求或重置共享实验，完整购物车仍按自己的入口解析。
        const experiment =
          IS_US && CART_POPOVER_EXPERIMENT_OVERRIDE === null
            ? await resolveBrowserCartExperiment(storeApi)
            : { status: 'disabled' as const };
        if (!current()) return;
        const policy = getCartExperimentPolicy(
          CART_POPOVER_EXPERIMENT_OVERRIDE ??
            (experiment.status === 'resolved' ? experiment.value : 1),
        ).couponPolicy;
        const scopeInput = {
          site: env.NEXT_PUBLIC_SITE_NAME,
          authenticated: Boolean(initial.token),
          userId: initial.userInfo?.id,
          token: initial.token,
          tokenReady: initial.clientTokenReady,
          userReady: initial.userInfoReady,
        };
        const scope = getCartOfferScope(scopeInput);
        if (IS_US && !scope) {
          set({ cartPopoverPriceStatus: 'idle' });
          return;
        }
        const sessionScope = getCartOfferScope({ ...scopeInput, userId: undefined });
        const intentAtStart = scope ? readCartOfferIntent(scope, sessionScope) : null;
        const guestScope = getCartOfferScope({
          site: env.NEXT_PUBLIC_SITE_NAME,
          authenticated: false,
          tokenReady: true,
          userReady: true,
        })!;
        // 登录后页面正等待新 AB 分组时，MiniCart 也承接游客取消；预读不消费页面的迁移记录。
        const guestSkipped =
          IS_US &&
          Boolean(initial.token) &&
          readGuestCartOfferSkip(guestScope, sessionScope, false);
        // 迁移可能因存储限额未落盘，校验实际缓存变化，不把它误判为用户改了优惠。
        const intentFingerprint = () =>
          JSON.stringify([
            scope && readCartOfferIntent(scope),
            sessionScope && readCartOfferIntent(sessionScope),
          ]);
        const initialIntentFingerprint = intentFingerprint();
        const selection = {
          ...createDefaultAutoOfferStore({
            couponPolicy: policy,
            authenticated: Boolean(initial.token),
          }),
          ...(intentAtStart || (guestSkipped ? { couponMode: 'skip' as const, code: '' } : {})),
        };
        const quote = await requestCartOfferQuote(selection, (params) => {
          if (!current()) return Promise.reject(new Error('Obsolete cart quote'));
          return getSubTotalPrice({ currency: currencyName, cart_ids: ids, ...params });
        });
        if (!current()) return;
        // 页面改券不会改变商品列表或报价版本，还需核对持久化意图，避免旧报价覆盖新选择。
        if (scope && intentFingerprint() !== initialIntentFingerprint) {
          set({ cartPopoverPriceStatus: 'error' });
          return;
        }
        if (scope) writeCartOfferIntent(scope, quote.selection, sessionScope);
        if (quote.status === 'error') {
          set({ subTotal: initialCartState.subTotal, cartPopoverPriceStatus: 'error' });
          return;
        }
        const settledIntent = intentFingerprint();
        // success 不是永久有效：结算时再次核对身份、商品、币种和优惠意图，覆盖异步校验期间的变化。
        quoteIsCurrent = () => current() && (!scope || intentFingerprint() === settledIntent);
        set({
          subTotal: { ...quote.data, applied_offer: quote.selection.applied_offer },
          cartPopoverOffer: quote.selection,
          cartPopoverPriceKey: priceKey,
          cartPopoverPriceStatus: 'success',
        });
      } catch (error) {
        if (current())
          set({ subTotal: initialCartState.subTotal, cartPopoverPriceStatus: 'error' });
        logger.error('Failed to get cart total price:', error);
      } finally {
        // 等实验或报价期间也可能跳转支付页；current() 同时阻止首次请求、券失效重试和迟到回写。
        // 只有本轮仍是最新调用时才收尾，防止旧请求结束后清掉新报价，也避免隐藏后残留 loading。
        if (version === quoteVersion && isHeaderHidden()) {
          set({
            cartPopoverPriceStatus: 'idle',
            cartPopoverOffer: undefined,
            cartPopoverPriceKey: undefined,
          });
        }
      }
    },
  };
};

function pendingLensGroups(data: {
  pending_lens_list?: ICartGroup[];
  pending_prescription_list?: ICartGroup[];
}): PendingCartGroup[] {
  if (!data.pending_prescription_list?.length) return data.pending_lens_list ?? [];
  return [
    ...(data.pending_lens_list ?? []),
    ...data.pending_prescription_list.map((group) => ({
      ...group,
      pendingKind: 'prescription' as const,
      // 新接口用独立集合表达待补处方，option 不再回传请求中的标识。
      carts: group.carts.map((item) => ({
        ...item,
        option: { ...item.option, is_pending_lens: 1 as const },
      })),
    })),
  ];
}
